import { useTheme } from '../theme/ThemeContext';
import { getMapControlsTheme } from '../theme/themeStyles';

interface MapControlsProps {
  onZoomIn: () => void;
  onZoomOut: () => void;
  onGetCurrentLocation: () => void;
  onZoomToField: () => void;
  onToggleFullscreen: () => void;
  onToggleHeatMap: () => void;
  onToggleTimeSeries: () => void;
  selectedField: any;
  hasIndexHeatMap: boolean;
  hasTimeSeries: boolean;
  isFullscreen: boolean;
  currentBaseLayer: 'satellite' | 'hybrid' | 'terrain' | 'googleSatellite' | 'googleHybrid' | 'googleTerrain';
  onBaseLayerChange: (layer: 'satellite' | 'hybrid' | 'terrain' | 'googleSatellite' | 'googleHybrid' | 'googleTerrain') => void;
  showBaseLayerMenu: boolean;
  onToggleBaseLayerMenu: () => void;
}

export default function MapControls({
  onZoomIn,
  onZoomOut,
  onGetCurrentLocation,
  onToggleFullscreen,
  currentBaseLayer,
  onBaseLayerChange,
  showBaseLayerMenu,
  onToggleBaseLayerMenu,
  isFullscreen,
}: MapControlsProps) {
  const { isDark } = useTheme();
  const mc = getMapControlsTheme(isDark);

  return (
    <div data-tour-id="map-controls" className="absolute top-4 left-4 z-40 flex flex-col gap-2">
      <div className={mc.shell}>
        <button
          type="button"
          onClick={onZoomIn}
          className={`${mc.btn} ${mc.btnBorder}`}
          title="Zoom In"
        >
          <i className={`ri-add-line text-lg font-bold ${mc.icon}`} />
        </button>
        <button
          type="button"
          onClick={onZoomOut}
          className={`${mc.btn}`}
          title="Zoom Out"
        >
          <i className={`ri-subtract-line text-lg font-bold ${mc.icon}`} />
        </button>
      </div>

      <div className="relative">
        <div className={mc.shell}>
          <button
            type="button"
            onClick={onToggleBaseLayerMenu}
            className={`${mc.btn} ${mc.btnBorder}`}
            title="Change Base Layer"
            aria-expanded={showBaseLayerMenu}
            aria-haspopup="menu"
          >
            <i className={`ri-stack-line text-base ${mc.icon}`} />
          </button>

          <button
            type="button"
            onClick={onGetCurrentLocation}
            className={`${mc.btn} ${mc.btnBorder}`}
            title="Current Location"
          >
            <i className={`ri-crosshair-line text-base ${mc.icon}`} />
          </button>

          <button
            type="button"
            onClick={onToggleFullscreen}
            className={`${mc.btn}`}
            title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
          >
            <i
              className={`ri-${isFullscreen ? 'fullscreen-exit' : 'fullscreen'}-line text-base ${mc.icon}`}
            />
          </button>
        </div>

        {showBaseLayerMenu && (
          <div className={mc.menuShell} role="menu" aria-label="Base layer">
            {(['satellite', 'hybrid', 'terrain', 'googleSatellite', 'googleHybrid', 'googleTerrain'] as const).map(
              (layer) => (
                <button
                  key={layer}
                  type="button"
                  role="menuitem"
                  onClick={() => onBaseLayerChange(layer)}
                  className={`${mc.menuItem} ${
                    currentBaseLayer === layer ? mc.menuItemActive : ''
                  }`}
                >
                  {layer === 'satellite' && 'Satellite (MapTiler)'}
                  {layer === 'hybrid' && 'Hybrid (MapTiler)'}
                  {layer === 'terrain' && 'Terrain (MapTiler)'}
                  {layer === 'googleSatellite' && 'Satellite (Google)'}
                  {layer === 'googleHybrid' && 'Hybrid (Google)'}
                  {layer === 'googleTerrain' && 'Terrain (Google)'}
                </button>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
}
